import React, { FunctionComponent, useEffect } from 'react'
import { Button, Heading, Text, Modal, useModal } from '@afterpaytouch/core'
import { TFunction } from 'next-i18next'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { clearPaymentOptionsError, useAppDispatch, useOfferDetailPBI, usePaymentOptionsError } from '../../state'
import styles from './style.module.css'
import { PaymentOptionsErrorModalKey, TerminalErrorCode } from '../../model'
import { TrackOnMount } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'

type PaymentOptionsModalErrorCode =
  | TerminalErrorCode.ErrorPCL
  | TerminalErrorCode.RiskDeclinedPCL
  | TerminalErrorCode.CreditFreezeDeclinedPCL
  | TerminalErrorCode.IdentityVerificationDeclinedPCL
  | TerminalErrorCode.AmountTooHighDeclinePCL
  | TerminalErrorCode.MaxActiveOrdersReachedDeclinePCL

interface PaymentOptionsErrorViewModel {
  title: string
  description: string
  showStillPayIn: boolean
}

interface PaymentOptionsErrorConfig {
  getTitle: (t: TFunction) => string
  getDescription: (t: TFunction, interval?: number) => string
  showStillPayIn: boolean
}

const PAYMENT_OPTIONS_ERROR_CONFIG: Record<PaymentOptionsModalErrorCode, PaymentOptionsErrorConfig> = {
  [TerminalErrorCode.ErrorPCL]: {
    getTitle: (t) => t('consumerLending:paymentOptionsErrorModal.errorPCL.title'),
    getDescription: (t, interval) => t('consumerLending:paymentOptionsErrorModal.errorPCL.description', { interval }),
    showStillPayIn: false,
  },
  [TerminalErrorCode.RiskDeclinedPCL]: {
    getTitle: (t) => t('consumerLending:paymentOptionsErrorModal.riskDeclinedPCL.title'),
    getDescription: (t, interval) => t('consumerLending:paymentOptionsErrorModal.riskDeclinedPCL.description', { interval }),
    showStillPayIn: true,
  },
  [TerminalErrorCode.CreditFreezeDeclinedPCL]: {
    getTitle: (t) => t('consumerLending:paymentOptionsErrorModal.creditFreezeDeclinedPCL.title'),
    getDescription: (t) => t('consumerLending:paymentOptionsErrorModal.creditFreezeDeclinedPCL.description'),
    showStillPayIn: true,
  },
  [TerminalErrorCode.IdentityVerificationDeclinedPCL]: {
    getTitle: (t) => t('consumerLending:paymentOptionsErrorModal.identityVerificationDeclinedPCL.title'),
    getDescription: (t) => t('consumerLending:paymentOptionsErrorModal.identityVerificationDeclinedPCL.description'),
    showStillPayIn: true,
  },
  [TerminalErrorCode.AmountTooHighDeclinePCL]: {
    getTitle: (t) => t('consumerLending:paymentOptionsErrorModal.amountTooHighDeclinePCL.title'),
    getDescription: (t, interval) => t('consumerLending:paymentOptionsErrorModal.amountTooHighDeclinePCL.description', { interval }),
    showStillPayIn: true,
  },
  [TerminalErrorCode.MaxActiveOrdersReachedDeclinePCL]: {
    getTitle: (t) => t('consumerLending:paymentOptionsErrorModal.maxActiveOrdersReachedDeclinePCL.title'),
    getDescription: (t, interval) => t('consumerLending:paymentOptionsErrorModal.maxActiveOrdersReachedDeclinePCL.description', { interval }),
    showStillPayIn: true,
  },
}

const selectPaymentOptionsErrorConfig = (error?: TerminalErrorCode): PaymentOptionsErrorConfig => {
  return PAYMENT_OPTIONS_ERROR_CONFIG[error as PaymentOptionsModalErrorCode] ?? PAYMENT_OPTIONS_ERROR_CONFIG[TerminalErrorCode.ErrorPCL]
}

const selectPaymentOptionsErrorViewModel = ({
  error,
  interval,
  t,
}: {
  error?: TerminalErrorCode
  interval?: number
  t: TFunction
}): PaymentOptionsErrorViewModel => {
  const selectedConfig = selectPaymentOptionsErrorConfig(error)
  return {
    title: selectedConfig.getTitle(t),
    description: selectedConfig.getDescription(t, interval),
    showStillPayIn: selectedConfig.showStillPayIn,
  }
}

export const PaymentOptionsErrorModal: FunctionComponent = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const paymentOptionsError = usePaymentOptionsError()
  const { visible, closeModal, showModal } = useModal(PaymentOptionsErrorModalKey)
  const { headingSize, textSize } = useCheckoutSizes()
  const offerDetailPBI = useOfferDetailPBI()
  const interval = offerDetailPBI?.numberOfInstallments
  const selectedErrorViewModel = selectPaymentOptionsErrorViewModel({
    error: paymentOptionsError?.error,
    interval,
    t,
  })

  // Sync modal visibility with paymentOptionsError state
  // This handles the case where paymentOptionsError is persisted but modal visibility is not
  useEffect(() => {
    if (paymentOptionsError !== undefined && !visible) {
      showModal()
    } else if (paymentOptionsError === undefined && visible) {
      closeModal()
    }
  }, [paymentOptionsError, visible, showModal, closeModal])

  const onClickContinue = (): void => {
    dispatch(clearPaymentOptionsError())
  }

  return (
    <Modal id={PaymentOptionsErrorModalKey} testNameSpace='payment-options-error' show={visible && paymentOptionsError !== undefined} onClose={onClickContinue}>
      <div className={styles.container}>
        <Modal.Header>
          <Heading size={headingSize}>{selectedErrorViewModel.title}</Heading>
        </Modal.Header>
        <Modal.Content>
          <div className={styles.text}>
            <Text size={textSize}>{selectedErrorViewModel.description}</Text>
          </div>
          {selectedErrorViewModel.showStillPayIn && (
            <div className={styles.text}>
              <Text size={textSize} bold>
                {t('consumerLending:paymentOptionsErrorModal.stillPayIn', { interval })}
              </Text>
            </div>
          )}
          <Button kind='Primary' padding='Fluid' size='Large' onClick={onClickContinue} testNameSpace='payment-options-error-modal'>
            {t('consumerLending:paymentOptionsErrorModal.continue')}
          </Button>
        </Modal.Content>
      </div>
      <TrackOnMount eventName={TrackingEvent.VIEWED_NON_TERMINAL_ERROR} eventProperties={{ errorCode: paymentOptionsError?.error }} />
    </Modal>
  )
}
